import Vue                  from 'vue';

import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { Route } from 'vue-router';

import CoordinatePair from '@/models/CoordinatePair';
import SiteReference from '@/models/SiteReference';

@Module
export default class UiState extends VuexModule {
  userLocation: CoordinatePair = {

    // In order for this to be reactive, each property has to be initialized... :(
    lat: -999,
    lng: -999
  }

  /**
   * Whether or not the tray is open
   */
  isTrayOpen = false;

  /**
   * Whether or not the app is in desktop mode
   *
   * IMPORTANT: This is driven by a window.resize observer and effectively functions as a CSS breakpoint
   *
   * @see
   */
  isDesktop = true;

  /**
   * When in mobile, whether the viewport is short
   *
   * IMPORTANT: This is driven by a window.resize observer and effectively functions as a CSS breakpoint
   */
  isShortMobile = false;

  //
  // Loading indicators
  //
  isGeoLocating = false;

  isKeywordSearching = false;

  isMapSearching = false;

  //
  // Gets set in Main.vue and it's true when both the site reference and site data have both loaded, in that order
  //
  isLoadingSite = false;

  autocompleteHasFocus = false;

  isSearchResultsRoute = false;

  isSiteRoute = false;

  recentSearches: Array<string> = [];

  recentSites: Array<SiteReference> = [];

  /**
   * A route to navigate to when back is clicked, or undefined if one doesn't exist
   */
  backRoute: Route = {
    path: '',
    hash: '',
    query: {},
    params: {},
    fullPath: '',
    matched: []
  };

  /**
   * The site number with a back route
   */
  siteNumberWithBackRoute: String = "";

  @Mutation
  assignUserLocation(userLocation: CoordinatePair) {
    this.userLocation = userLocation;
  }

  @Action({ commit: 'assignUserLocation' })
  async setUserLocation(userLocation: CoordinatePair) {
    return userLocation;
  }

  @Mutation
  assignIsTrayOpen(isTrayOpen: boolean) {
    this.isTrayOpen = isTrayOpen;
  }

  @Action({ commit: 'assignIsTrayOpen' })
  async setIsTrayOpen(isTrayOpen: boolean) {
    return isTrayOpen;
  }

  @Mutation
  assignIsDesktop(isDesktop: boolean) {
    this.isDesktop = isDesktop;
  }

  @Action({ commit: 'assignIsDesktop' })
  async setIsDesktop(isDesktop: boolean) {
    return isDesktop;
  }

  @Mutation
  assignIsShortMobile(isShortMobile: boolean) {
    this.isShortMobile = isShortMobile;
  }

  @Action({ commit: 'assignIsShortMobile' })
  async setIsShortMobile(isShortMobile: boolean) {
    return isShortMobile;
  }

  @Mutation
  mutateBackRoute(backRoute: Route) {
    this.backRoute = backRoute;
  }

  @Action({ commit: 'mutateBackRoute' })
  async setBackRoute(backRoute: Route) {
    return backRoute;
  }

  @Mutation
  mutateSiteNumberWithBackRoute(siteNumber: string) {
    this.siteNumberWithBackRoute = siteNumber;
  }

  @Action({ commit: 'mutateSiteNumberWithBackRoute' })
  async setSiteNumberWithBackRoute(siteNumber: string) {
    return siteNumber;
  }

  @Mutation
  mutateIsLoadingSite(isLoadingSite: boolean) {
    this.isLoadingSite = isLoadingSite;
  }

  @Action({ commit: 'mutateIsLoadingSite' })
  async setIsLoadingSite(isLoadingSite: boolean) {
    return isLoadingSite;
  }

  @Mutation
  mutateAutocompleteHasFocus(autocompleteHasFocus: boolean) {
    this.autocompleteHasFocus = autocompleteHasFocus;
  }

  @Action({ commit: 'mutateAutocompleteHasFocus' })
  async setAutocompleteHasFocus(autocompleteHasFocus: boolean) {
    return autocompleteHasFocus;
  }

  @Mutation
  mutateIsSearchResultsRoute(isSearchResultsRoute: boolean) {
    this.isSearchResultsRoute = isSearchResultsRoute;
  }

  @Action({ commit: 'mutateIsSearchResultsRoute' })
  async setIsSearchResultsRoute(isSearchResultsRoute: boolean) {
    return isSearchResultsRoute;
  }

  @Mutation
  mutateIsSiteRoute(isSiteRoute: boolean) {
    this.isSiteRoute = isSiteRoute;
  }

  @Action({ commit: 'mutateIsSiteRoute' })
  async setIsSiteRoute(isSiteRoute: boolean) {
    return isSiteRoute;
  }

  @Mutation
  mutateAddRecentSearch(recentSearch: string) {

    const recentSearches = [... this.recentSearches];

    // If search is already part of the list, snip it out
    const previousLocation = recentSearches.indexOf(recentSearch);
    if (previousLocation !== -1) {
      recentSearches.splice(previousLocation, 1);
    }

    // Add this search to the front of the list
    recentSearches.unshift(recentSearch);

    // Workaround weird Vue array reactive issue with Vue.set
    recentSearches.forEach((search: string, i: number) => {
      Vue.set(this.recentSearches, i, search);
    });

    // Put it back in local storage
    localStorage.setItem('recent-searches', JSON.stringify(this.recentSearches));
  }

  @Action({ commit: 'mutateAddRecentSearch' })
  async setRecentSearch(recentSearch: string) {
    return recentSearch;
  }

  @Mutation
  mutateInitRecentSearches() {
    let recentSearchesJson = localStorage.getItem('recent-searches');
    const recentSearches = recentSearchesJson ? JSON.parse(recentSearchesJson) : [];

    // Workaround weird Vue array reactive issue with Vue.set
    recentSearches.forEach((search: string, i: number) => {
      Vue.set(this.recentSearches, i, search);
    });
  }

  @Action({ commit: 'mutateInitRecentSearches' })
  async initRecentSearches() {
    return;
  }

  // Do Vuex state first and get that dialed in
  //
  // then use localstorage

  @Mutation
  mutateAddRecentSite(recentSite: SiteReference) {

    const recentSites = [... this.recentSites];

    // If search is already part of the list, snip it out
    const previousLocation = recentSites.findIndex((s) => s.siteNumber === recentSite.siteNumber);
    if (previousLocation !== -1) {
      recentSites.splice(previousLocation, 1);
    }

    // Add this search to the front of the list
    recentSites.unshift(recentSite);

    // Workaround weird Vue array reactive issue with Vue.set
    recentSites.forEach((site: SiteReference, i: number) => {
      Vue.set(this.recentSites, i, site);
    });

    // Put it back in local storage
    localStorage.setItem('recent-sites', JSON.stringify(this.recentSites));
  }

  @Action({ commit: 'mutateAddRecentSite' })
  async addRecentSite(recentSite: SiteReference) {
    return recentSite;
  }

  @Mutation
  mutateInitRecentSites() {
    let recentSearchesJson = localStorage.getItem('recent-sites');
    const recentSites = recentSearchesJson ? JSON.parse(recentSearchesJson) : [];

    // Workaround weird Vue array reactive issue with Vue.set
    recentSites.forEach((search: string, i: number) => {
      Vue.set(this.recentSites, i, search);
    });
  }

  @Action({ commit: 'mutateInitRecentSites' })
  async initRecentSites() {
    return;
  }

}
